import cns from 'classnames';
import download from 'downloadjs';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { RuntimeContext } from '../../../../../common/RuntimeContext';
import { getFileWithSignedUrl } from '../../../../shared/actions/Files';
import VerbButton, { FONT_AWESOME_ICON_WEIGHT, VERB_BUTTON_TYPE } from '../../../../shared/components/VerbButton';
import VerbHeading from '../../../../shared/components/VerbHeading';
import VerbText from '../../../../shared/components/VerbText';
import VerbCircularProgressbar from '../../../../shared/components/widgets/CircularProgressbar';
import { COLLECTION_TYPES, DEFAULT_IMAGE_URLS, SEGMENT_EVENTS } from '../../../../shared/Constants';
import { attachCloudinaryOptionsToImageSource, createUserCertificate } from '../../../../shared/utils/ImageUtils';
import { makeBannerBackgroundStyle } from '../../../../shared/utils/StylingUtils';

class BannerUserCollectionWidget extends Component {
    static propTypes = {
        handleDelayedCreateLearningAssetsForUserCollection: PropTypes.func,
        history: PropTypes.object,
        userLearningAssets: PropTypes.object,
        loading: PropTypes.bool,
        isNewUserCollection: PropTypes.bool,
        userCollection: PropTypes.object,
        intl: PropTypes.shape({
            formatNumber: PropTypes.func
        }),
        loggedInUser: PropTypes.object,
        isHome: PropTypes.bool,
        userCompany: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        isTeamLead: PropTypes.bool,
    };

    static contextType = RuntimeContext;

    _getCTATextObject = () => {
        const { userCollection = {}, userLearningAssets, loading, isNewUserCollection } = this.props;
        const length = userCollection.content ? userCollection.content.length : 0;
        if (loading) {
            return { loading: true };
        }

        if (!length || !userLearningAssets || userCollection.percentComplete === 100) {
            return { noAssets: true };
        }
        if (!userCollection.percentComplete && userCollection.status !== 'IN_PROGRESS') {
            return {
                href: {
                    pathname: `/workspace/${userCollection.content[0].userLearningAssetId}`,
                    state: {
                        userCollection: {
                            id: userCollection._id,
                            started: true
                        }
                    }
                },
                buttonProps: {
                    textId: 'BUTTON_BEGIN',
                    defaultMessage: 'BEGIN'
                },
                textProps: {
                    messageId: 'BUTTON_BEGIN',
                    defaultMessage: 'BEGIN'
                }
            };
        }
        for (let i = 0; i < length; i += 1) {
            const currentAsset = userLearningAssets[userCollection.content[i].userLearningAssetId];
            if (currentAsset && !currentAsset.isCompleted) {
                return {
                    href: {
                        pathname: `/workspace/${userCollection.content[i].userLearningAssetId}`,
                        state: {
                            userCollection: {
                                id: userCollection._id,
                                started: false
                            }
                        }
                    },
                    buttonProps: {
                        textId: isNewUserCollection ? 'BUTTON_BEGIN' : 'BUTTON_CONTINUE',
                        defaultMessage: isNewUserCollection ? 'BEGIN' : 'CONTINUE'
                    },
                    textProps: {
                        messageId: 'CONTINUE_COLLECTION_HEADING',
                        defaultMessage: 'PICK UP WHERE YOU LEFT OFF'
                    }
                };
            }
        }
        return {};
    };

    _renderPercentText = () => {
        const {
            intl: { formatNumber },
            userCollection
        } = this.props;
        if (userCollection.percentComplete > 0 || userCollection.status === 'IN_PROGRESS') {
            return (
                <text className={'CircularProgressbar-text'} x={50} y={50}>
                    <tspan key={'percent'} y={'45'} x={'50'} className={'percent'}>
                        {formatNumber(userCollection.percentComplete / 100, { style: 'percent' })}
                    </tspan>
                    <tspan key={'percent-label'} y={'59'} x={'50'} className={'percent-label'}>
                        <VerbText messageId={'HEADING_COMPLETE'} defaultMessage={'COMPLETE'} />
                    </tspan>
                </text>
            );
        }
        return (
            <foreignObject className={'CircularProgressbar-text'} width={85} height={150} x={28} y={35}>
                <span className={'not-yet-started'}>
                    <VerbText messageId={'BANNER_USER_COLLECTION_NOT_STARTED'} defaultMessage={'NOT YET STARTED'} />
                </span>
            </foreignObject>
        );
    };

    _downloadResources = (resources) => {
        const { userCompany, isVerbAdmin } = this.props;
        resources.map((item) => getFileWithSignedUrl(this.context.pathsWeb, item.url.split(' ').join('+')));
    };

    _downloadCert = (certUrl, fileName) => {
        const { userCompany, isVerbAdmin } = this.props;
        return fetch(certUrl)
            .then((res) => res.blob())
            .then((blob) => download(blob, fileName));
    };

    _getBannerCopyForUserCollection = (userCollection = {}) => {
        const bannerCopy = {
            link: `/collection/${userCollection.collectionPermalink || userCollection.collectionId}`,
            name: userCollection.name || userCollection.collectionName,
            subtitle: userCollection.collectionSubtitle,
        };
        return bannerCopy;
    };

    _renderBannerAtCompletion = (backgroundStyle, userCollection, loggedInUser = {}) => {
        const { isHome } = this.props;
        const userName = `${loggedInUser.firstName} ${loggedInUser.lastName}`;
        let collectionCertificate;
        let certName;
        if (userName && userCollection.collectionName) {
            certName = `${userCollection.collectionName.split(' ').join('_')}_Certificate`;
            const date = userCollection.completedOn || userCollection.updatedOn;
            collectionCertificate = createUserCertificate(userName, userCollection.collectionName, date, 'COLLECTION');
        }

        const { name, link, subtitle } = this._getBannerCopyForUserCollection(
            userCollection,
        );

        return (
            <div className={'bannerUserCollectionWidget'} style={backgroundStyle}>
                <div className={'container'}>
                    <div className={'col-sm-3 col-12'}>
                        <VerbCircularProgressbar
                            percentage={userCollection.percentComplete}
                            textForPercentage={() => null}
                            imageForPercentage={this._renderPercentText}
                        />
                    </div>
                    <div className={'col-sm-9 col-12'}>
                        <div className={'col-sm-12'}>
                            <h6>YOU'VE COMPLETED</h6>
                            <Link to={link}>
                                <h1 className={'collection-name'}>{name}</h1>
                            </Link>
                            <span className={'collection-subtitle'}>{subtitle}</span>
                        </div>
                        {userCollection.type === COLLECTION_TYPES.STANDARD && (
                            <div className={'col-12 col-sm-7'}>
                                <VerbButton
                                    textId={'CERTIFICATE_COLLECTION_HEADING'}
                                    defaultMessage={'CERTIFICATE OF COMPLETION'}
                                    className={'verb-button-certificate'}
                                    buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                                    fontAwesomeIcon={'fa-download'}
                                    fontAwesomeIconWeight={FONT_AWESOME_ICON_WEIGHT.SOLID}
                                    onClick={() => this._downloadCert(collectionCertificate, certName)}
                                />
                            </div>
                        )}
                        {userCollection.resources && userCollection.resources.length && !isHome ? (
                            <div className={'col-12 col-sm-5'}>
                                <VerbButton
                                    textId={'REFERENCES_COLLECTION_HEADING'}
                                    defaultMessage={'REFERENCES'}
                                    className={'verb-button-references'}
                                    buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                                    fontAwesomeIcon={'fa-download'}
                                    fontAwesomeIconWeight={FONT_AWESOME_ICON_WEIGHT.SOLID}
                                    onClick={() => this._downloadResources(userCollection.resources)}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    };

    /**
     *
     * This function is not optimized. Calls getCollection via the home component. But it fixes the issue.
     * @param {Object} href
     * @returns {*}
     * @private
     */

    _getClickHandlerForActionButton = (href) => {
        const { userCollection, handleDelayedCreateLearningAssetsForUserCollection, history } = this.props;

        if (!userCollection || !userCollection.content) {
            return () => {};
        }

        if (!userCollection.percentComplete && !userCollection.content[0].userLearningAssetId) {
            return () => handleDelayedCreateLearningAssetsForUserCollection(userCollection._id);
        }
        return () =>
            history.push(href, {
                userCollection: { id: userCollection._id }
            });
    };

    render() {
        const {
            userCollection = {},
            loading,
            loggedInUser,
            isHome = false,
            isTeamLead = false,
        } = this.props;

        const textObject = this._getCTATextObject();

        if (loading) {
            return <div className={'bannerUserCollectionWidget loading'} />;
        }
        const { buttonProps = {}, textProps, href } = textObject;
        const clickHandler = this._getClickHandlerForActionButton(href);
        const coverImageUrl = userCollection.coverImageUrl || DEFAULT_IMAGE_URLS.COLLECTION_BANNER_COVER_IMAGE;
        const backgroundUrl = attachCloudinaryOptionsToImageSource(coverImageUrl, {
            color: '000000',
            colorize: 40
        });

        const backgroundStyle = makeBannerBackgroundStyle(backgroundUrl);

        const { name, link, subtitle } = this._getBannerCopyForUserCollection(
            userCollection,
        );
        if (textObject.noAssets) {
            return this._renderBannerAtCompletion(backgroundStyle, userCollection, loggedInUser);
        }

        return (
            <div className={'bannerUserCollectionWidget'} style={backgroundStyle}>
                <div className={'container'}>
                    <div className={'col-sm-3 col-12'}>
                        <VerbCircularProgressbar
                            percentage={userCollection.percentComplete}
                            textForPercentage={() => null}
                            imageForPercentage={this._renderPercentText}
                        />
                    </div>
                    <div className={'col-sm-9 col-12'}>
                        <div className={'col-sm-12'}>
                            {textProps && (
                                <VerbHeading
                                    hNumber={6}
                                    messageId={textProps.messageId}
                                    defaultMessage={textProps.defaultMessage}
                                />
                            )}
                            <Link to={link}>
                                <h1 className={'collection-name'}>{name}</h1>
                            </Link>
                            <span className={'collection-subtitle'}>{subtitle}</span>
                        </div>
                        <div className={'col-12 col-sm-4'}>
                            <VerbButton
                                textId={buttonProps.textId}
                                defaultMessage={buttonProps.defaultMessage}
                                onClick={clickHandler}
                            />
                        </div>
                        {userCollection.resources && userCollection.resources.length && !isHome ? (
                            <div className={'col-12 col-sm-6'}>
                                <VerbButton
                                    textId={'REFERENCES_COLLECTION_HEADING'}
                                    defaultMessage={'REFERENCES'}
                                    className={cns({ 'verb-button-locked': !isTeamLead })}
                                    buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                                    fontAwesomeIcon={isTeamLead ? 'fa-download' : 'fa-lock-alt'}
                                    disabled={!isTeamLead}
                                    fontAwesomeIconWeight={FONT_AWESOME_ICON_WEIGHT.SOLID}
                                    onClick={() => this._downloadResources(userCollection.resources)}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    }
}
export const mapStateToProps = (state) => {
    const {
        auth: { loggedInUser, isVerbAdmin },
        learningAssets,
        userLearningAssets,
        userCollections,
        companies
    } = state;

    const userCompany = companies && companies.byId && companies.byId[loggedInUser.companyId];
    const loading = userLearningAssets.loading && userCollections.loading;

    return {
        loggedInUser,
        learningAssets: learningAssets.byId,
        userLearningAssets: userLearningAssets.byId,
        loading,
        isVerbAdmin,
        userCompany
    };
};
export const BannerUserCollectionWidgetComponent = BannerUserCollectionWidget;
export default withRouter(connect(mapStateToProps, null)(injectIntl(BannerUserCollectionWidget)));
